import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Globe, Building2, Workflow, ShieldCheck, ArrowRight, ArrowLeft,
  SkipForward, LogOut, CheckCircle2, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import FlowMapper from '@/components/evidence/FlowMapper';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

const STEP_META = [
  { key: 'website', label: '0.1 Website Scan', icon: Globe },
  { key: 'workspace', label: '0.2 Workspace Discovery', icon: Building2 },
  { key: 'systems', label: '0.3 Flow Mapper', icon: Workflow },
  { key: 'review', label: '0.4 Review & Start', icon: ShieldCheck },
];

const STATUS_STYLES = {
  pending: 'bg-slate-100 text-slate-600',
  stubbed: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  skipped: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-700',
  timed_out: 'bg-red-100 text-red-700',
};

const StepBadge = ({ status }) => (
  <Badge className={STATUS_STYLES[status] || STATUS_STYLES.pending} data-testid={`step-status-${status}`}>
    {status}
  </Badge>
);

const EvidenceDiscovery = () => {
  const navigate = useNavigate();
  const { orgProfile, logout, getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [assessmentId, setAssessmentId] = useState(null);
  const [status, setStatus] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [attested, setAttested] = useState(false);
  const [connectors, setConnectors] = useState(null);
  const pollRef = useRef(null);

  const authHeader = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const applyStatus = useCallback((data) => {
    setStatus(data);
    if (data.discovery_status === 'locked' || data.discovery_status === 'not_applicable') {
      navigate('/assessment', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!orgProfile) {
      navigate('/org-profile', { replace: true });
      return;
    }
    (async () => {
      try {
        const start = await axios.post(`${API}/assessment/start`, {}, authHeader());
        const id = start.data.assessment_id;
        setAssessmentId(id);
        if (start.data.discovery_status === 'not_applicable' || start.data.discovery_status === 'locked') {
          navigate('/assessment', { replace: true });
          return;
        }
        await axios.post(`${API}/evidence/${id}/start`, {}, authHeader());
        const res = await axios.get(`${API}/evidence/${id}/status`, authHeader());
        applyStatus(res.data);
        await loadConnectors(id);
        const params = new URLSearchParams(window.location.search);
        if (params.get('connector')) {
          const outcome = params.get('status');
          if (outcome === 'connected') {
            toast.success(`${params.get('connector')} connected — run the read-only scan`);
            setStepIndex(1);
          } else {
            toast.error(`${params.get('connector')} consent was not completed`);
            setStepIndex(1);
          }
        }
      } catch (e) {
        toast.error('Could not open Evidence Discovery');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgProfile]);

  const websiteRun = (status?.runs || []).filter((r) => r.type === 'website').slice(-1)[0];
  const workspaceRun = (status?.runs || []).filter((r) => r.type === 'workspace').slice(-1)[0];
  const steps = status?.steps || {};
  const scanning = (websiteRun && ['queued', 'running'].includes(websiteRun.status))
    || (workspaceRun && ['queued', 'running'].includes(workspaceRun.status));
  const progress = status?.progress || {};
  const findings = websiteRun?.findings || [];
  const issues = findings.filter((f) => ['fail', 'partial'].includes(f.status));

  // Poll while the website scan is in flight (same pattern as DOCX generation).
  useEffect(() => {
    if (!assessmentId || !scanning) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/evidence/${assessmentId}/status`, authHeader());
        setStatus(res.data);
      } catch (e) { /* transient */ }
    }, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [assessmentId, scanning, authHeader]);

  const loadConnectors = async (id) => {
    try {
      const res = await axios.get(`${API}/evidence/${id || assessmentId}/connectors`, authHeader());
      setConnectors(res.data);
    } catch (e) { setConnectors(null); }
  };

  const connectProvider = async (provider) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/evidence/${assessmentId}/connect/${provider}`, {}, authHeader());
      window.location.assign(res.data.authorize_url);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.code === 'CONNECTOR_NOT_CONFIGURED'
        ? `${provider} is not configured on this environment yet`
        : 'Could not start the connection');
      setBusy(false);
    }
  };

  const runWorkspaceScan = async (provider) => {
    setBusy(true);
    try {
      await axios.post(`${API}/evidence/${assessmentId}/scan/workspace?provider=${provider}`, {}, authHeader());
      const res = await axios.get(`${API}/evidence/${assessmentId}/status`, authHeader());
      setStatus(res.data);
      toast.success('Read-only scan started');
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Could not start the scan');
    } finally { setBusy(false); }
  };

  const revokeProvider = async (provider) => {
    setBusy(true);
    try {
      await axios.post(`${API}/evidence/${assessmentId}/connectors/${provider}/revoke`, {}, authHeader());
      await loadConnectors();
      toast.success(`${provider} access revoked and token deleted`);
    } catch (e) { toast.error('Could not revoke access'); }
    finally { setBusy(false); }
  };

  const useDnsOnly = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/evidence/${assessmentId}/scan/workspace/dns-only`, {}, authHeader());
      setStatus((await axios.get(`${API}/evidence/${assessmentId}/status`, authHeader())).data);
      toast.success('Using DNS evidence for workspace discovery');
      return res;
    } catch (e) { toast.error('Could not record the DNS-only path'); }
    finally { setBusy(false); }
  };

  const runWebsiteScan = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/evidence/${assessmentId}/scan/website`,
        { url: websiteUrl, attested },
        authHeader(),
      );
      const res = await axios.get(`${API}/evidence/${assessmentId}/status`, authHeader());
      setStatus(res.data);
      toast.success('Scan started — this takes up to two minutes');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail?.message || (detail?.code === 'SCAN_LIMIT_ASSESSMENT'
        ? 'You have used all 3 scans for this assessment'
        : detail?.code === 'SCAN_LIMIT_IP'
          ? 'Daily scan limit reached — try again tomorrow'
          : 'Could not start the scan'));
    } finally {
      setBusy(false);
    }
  };

  const skipStep = async (step) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/evidence/${assessmentId}/skip`, { step }, authHeader());
      setStatus(res.data);
      setStepIndex((i) => Math.min(i + 1, STEP_META.length - 1));
    } catch (e) {
      toast.error('Could not skip this step');
    } finally {
      setBusy(false);
    }
  };

  const skipAll = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/evidence/${assessmentId}/skip`, {}, authHeader());
      setStatus(res.data);
      setStepIndex(STEP_META.length - 1);
    } catch (e) {
      toast.error('Could not skip Evidence Discovery');
    } finally {
      setBusy(false);
    }
  };

  const lockAndStart = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/evidence/${assessmentId}/lock`, {}, authHeader());
      toast.success('Evidence frozen — starting the assessment');
      applyStatus(res.data);
    } catch (e) {
      toast.error('Could not lock Evidence Discovery');
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#003366]" />
      </div>
    );
  }

  const StepIcon = STEP_META[stepIndex].icon;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="evidence-discovery-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <img src={LOGO_URL} alt="CodeXero" className="h-8" />
          <div className="flex items-center gap-3">
            <Badge className="bg-[#003366] text-white">Stage 0 — Evidence Discovery</Badge>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} data-testid="evidence-logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-[700] text-[#003366]">Evidence Discovery</h1>
          <p className="text-base text-slate-600 mt-1 max-w-3xl">
            Optional. Anything we discover appears as <strong>hint chips</strong> next to the relevant
            questions. Evidence never changes your score, and we never store personal data values —
            only counts, categories and locations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-8">
          {STEP_META.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setStepIndex(i)}
              data-testid={`evidence-step-tab-${s.key}`}
              className={`text-left px-3 py-3 rounded-sm border transition-colors ${
                i === stepIndex ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-slate-700 border-slate-200 hover:border-[#003366]'
              }`}
            >
              <div className="text-sm font-[600]">{s.label}</div>
              {s.key !== 'review' && (
                <div className="mt-2"><StepBadge status={steps[s.key] || 'pending'} /></div>
              )}
            </button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <StepIcon className="h-5 w-5 text-[#003366]" />
            <h2 className="text-lg font-[700] text-[#003366]">{STEP_META[stepIndex].label}</h2>
          </div>

          {stepIndex === 0 && (
            <div className="space-y-4" data-testid="evidence-website-step">
              <div>
                <Label htmlFor="website-url">Public website URL</Label>
                <Input
                  id="website-url"
                  placeholder="https://www.yourcompany.com"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  className="mt-1 max-w-xl"
                  data-testid="evidence-website-url-input"
                />
                <p className="text-sm text-slate-500 mt-2">
                  We read up to 25 public pages plus your DNS records — privacy notice, consent and
                  cookie handling, grievance contact, rights routes, security headers and SPF/DMARC/DKIM.
                </p>
              </div>

              <label className="flex items-start gap-2 text-sm text-slate-700 max-w-2xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={attested}
                  onChange={(e) => setAttested(e.target.checked)}
                  className="mt-1"
                  data-testid="evidence-attestation-checkbox"
                />
                I confirm I am authorised to scan this website on behalf of my organisation.
              </label>

              {websiteRun && (
                <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="evidence-website-run-card">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-mono text-slate-700">{websiteRun.target}</span>
                    <Badge className={
                      websiteRun.status === 'ready' ? 'bg-green-100 text-green-700'
                        : websiteRun.status === 'failed' ? 'bg-red-100 text-red-700'
                          : websiteRun.status === 'timed_out' ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-700'
                    } data-testid="evidence-run-status">
                      {websiteRun.status}
                    </Badge>
                    {scanning && <Loader2 className="h-4 w-4 animate-spin text-[#003366]" />}
                    {websiteRun.partial && (
                      <span className="text-amber-700">partial results</span>
                    )}
                  </div>
                  <div className="text-sm text-slate-600" data-testid="evidence-scan-progress">
                    {progress.pages_scanned || 0} page(s) read · {progress.checks_done || 0} check(s) done ·
                    DNS {progress.dns_done ? 'done' : 'pending'}
                  </div>
                  {websiteRun.status === 'ready' && (
                    <div className="text-sm text-slate-700" data-testid="evidence-findings-summary">
                      {findings.length} check(s) evaluated — <strong>{issues.length}</strong> need attention.
                      {websiteRun.vendors?.length > 0 && (
                        <> {websiteRun.vendors.length} third-party service(s) detected.</>
                      )}
                    </div>
                  )}
                  {websiteRun.robots_blocked?.length > 0 && (
                    <div className="text-xs text-slate-500">
                      {websiteRun.robots_blocked.length} path(s) skipped because robots.txt disallows them.
                    </div>
                  )}
                  {websiteRun.reason && (
                    <div className="text-xs text-red-600">{websiteRun.reason}</div>
                  )}
                </div>
              )}

              {issues.length > 0 && (
                <div className="border border-slate-200 rounded-sm divide-y" data-testid="evidence-findings-list">
                  {issues.slice(0, 8).map((f) => (
                    <div key={f.check_id} className="p-3 flex items-start gap-3">
                      <Badge className={f.status === 'fail' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}>
                        {f.status}
                      </Badge>
                      <div>
                        <div className="text-sm font-[600] text-slate-800">{f.title}</div>
                        <div className="text-sm text-slate-600">{f.evidence_text}</div>
                        {f.question_number && (
                          <div className="text-xs text-slate-400 mt-1">
                            Hint chip for {f.question_number} ({f.pillar})
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={runWebsiteScan}
                  disabled={busy || !websiteUrl || !attested || scanning}
                  className="bg-[#003366] hover:bg-[#002244] rounded-sm"
                  data-testid="evidence-run-website-scan-btn"
                >
                  {busy || scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Globe className="h-4 w-4 mr-2" />}
                  {websiteRun ? 'Re-scan' : 'Run Scan'}
                </Button>
                <Button variant="outline" onClick={() => skipStep('website')} disabled={busy} className="rounded-sm" data-testid="evidence-skip-website-btn">
                  <SkipForward className="h-4 w-4 mr-2" /> Skip this step
                </Button>
              </div>
            </div>
          )}

          {stepIndex === 1 && (
            <div className="space-y-4" data-testid="evidence-workspace-step">
              {connectors?.dns_only ? (
                <div className="space-y-3" data-testid="evidence-dns-only-block">
                  <div className="border-l-4 border-slate-400 bg-slate-50 p-4 rounded-sm text-sm text-slate-700">
                    {connectors.dns_only_reason}
                  </div>
                  <Button onClick={useDnsOnly} disabled={busy} className="bg-[#003366] hover:bg-[#002244] rounded-sm" data-testid="evidence-dns-only-btn">
                    Use DNS evidence only
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {(connectors?.providers || []).map((p) => (
                    <div key={p.provider} className={`border rounded-sm p-4 ${p.recommended ? 'border-[#008080] bg-[#008080]/5' : 'border-slate-200'}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-[700] text-[#003366] capitalize">{p.provider}</span>
                        {p.recommended && <Badge className="bg-[#008080] text-white">Matches your MX records</Badge>}
                        {connectors.connected?.includes(p.provider) && (
                          <Badge className="bg-green-100 text-green-700">connected</Badge>
                        )}
                        {!p.configured && <Badge className="bg-slate-100 text-slate-600">not configured</Badge>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Read-only scopes: {p.scopes.length}. {p.limitation ? p.limitation : ''}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => connectProvider(p.provider)}
                          disabled={busy || !p.configured}
                          className="bg-[#003366] hover:bg-[#002244] rounded-sm"
                          data-testid={`evidence-connect-${p.provider}-btn`}
                        >
                          {connectors.connected?.includes(p.provider) ? 'Reconnect' : 'Connect'}
                        </Button>
                        {connectors.connected?.includes(p.provider) && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => runWorkspaceScan(p.provider)} disabled={busy} className="rounded-sm" data-testid={`evidence-scan-${p.provider}-btn`}>
                              Run read-only scan
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => revokeProvider(p.provider)} disabled={busy} className="text-red-600" data-testid={`evidence-revoke-${p.provider}-btn`}>
                              Revoke access
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {workspaceRun && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm space-y-1" data-testid="evidence-workspace-run-card">
                  <div className="flex items-center gap-2">
                    <span className="capitalize font-[600]">{workspaceRun.provider}</span>
                    <Badge className={workspaceRun.status === 'ready' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                      {workspaceRun.status}
                    </Badge>
                    {['queued', 'running'].includes(workspaceRun.status) && <Loader2 className="h-4 w-4 animate-spin text-[#003366]" />}
                  </div>
                  <div className="text-slate-600" data-testid="evidence-workspace-progress">
                    {workspaceRun.progress?.files_enumerated || 0} file(s) listed ·
                    {' '}{workspaceRun.progress?.files_opened || 0} sampled
                    {workspaceRun.coverage?.coverage_partial ? ' · partial coverage' : ''}
                  </div>
                  {workspaceRun.pii_counts && Object.keys(workspaceRun.pii_counts).length > 0 && (
                    <div className="text-slate-700">
                      Personal-data categories found: {Object.entries(workspaceRun.pii_counts).map(([k, v]) => `${k} (${v})`).join(', ')}
                      {' '}— counts only, no values stored
                    </div>
                  )}
                </div>
              )}

              <p className="text-sm text-slate-500">
                Read-only access. We store counts and file locations only — never the data itself.
                Your access token is deleted the moment the scan ends.
              </p>
              <Button variant="outline" onClick={() => skipStep('workspace')} disabled={busy} className="rounded-sm" data-testid="evidence-skip-workspace-btn">
                <SkipForward className="h-4 w-4 mr-2" /> Skip this step
              </Button>
            </div>
          )}

          {stepIndex === 2 && (
            <div data-testid="evidence-systems-step">
              <FlowMapper
                assessmentId={assessmentId}
                authHeader={authHeader}
                evidenceSkipped={steps.website === 'skipped' && steps.workspace === 'skipped'}
                onSkip={() => skipStep('systems')}
                onCompleted={async () => {
                  const res = await axios.get(`${API}/evidence/${assessmentId}/status`, authHeader());
                  setStatus(res.data);
                  setStepIndex(3);
                }}
              />
            </div>
          )}

          {stepIndex === 3 && (
            <div className="space-y-5" data-testid="evidence-review-step">
              <div className="space-y-2">
                {STEP_META.slice(0, 3).map((s) => (
                  <div key={s.key} className="flex items-center justify-between border border-slate-200 rounded-sm px-4 py-3">
                    <span className="text-sm text-slate-700">{s.label}</span>
                    <StepBadge status={steps[s.key] || 'pending'} />
                  </div>
                ))}
              </div>
              {findings.length > 0 && (
                <div className="text-sm text-slate-700" data-testid="evidence-review-summary">
                  {findings.length} check(s) evaluated · <strong>{issues.length}</strong> hint chip(s) will be
                  attached to the matching questions when you freeze the evidence.
                </div>
              )}
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-sm text-amber-900">
                <Lock className="h-4 w-4 inline mr-1 -mt-0.5" />
                Starting the assessment freezes the evidence. Re-scans are disabled afterwards, and
                anything that finishes late appears in the report only — never as a hint.
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={lockAndStart} disabled={busy} className="bg-[#003366] hover:bg-[#002244] rounded-sm" data-testid="evidence-lock-start-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Freeze evidence & start the assessment
                </Button>
                <Button variant="outline" onClick={skipAll} disabled={busy} className="rounded-sm" data-testid="evidence-skip-all-btn">
                  <SkipForward className="h-4 w-4 mr-2" /> Skip Evidence Discovery
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between mt-6">
          <Button
            variant="ghost"
            onClick={() => setStepIndex((i) => Math.max(i - 1, 0))}
            disabled={stepIndex === 0}
            data-testid="evidence-prev-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <Button
            variant="outline"
            onClick={() => setStepIndex((i) => Math.min(i + 1, STEP_META.length - 1))}
            disabled={stepIndex === STEP_META.length - 1}
            className="rounded-sm"
            data-testid="evidence-next-btn"
          >
            Next <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default EvidenceDiscovery;
